import FalconCardHeader from 'components/common/FalconCardHeader';
import { getPersistentFacility, setPersistentFacility } from 'helpers/persistence';
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Row,
  Col,
  Badge,
  Modal,
  Button,
  Form,
  InputGroup,
  Table
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import BasicECharts from 'components/common/BasicEChart';
import { PieChart } from 'echarts/charts';
import {
  GridComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from 'providers/AppProvider';
import UnverifiedPrescriptionModal from './UnverifiedPrescriptionModal';

const COMP_KEY = 'order_data';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  PieChart,
  CanvasRenderer
]);

const toNumber = value => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const MODAL_SORT_OPTIONS = [
  { value: 'OVER_21_DESC', label: 'Unverified (> 21 Days) - Descending' },
  { value: 'UNVERIFIED_DESC', label: 'Unverified Total - Descending' },
  { value: 'PATIENTS_DESC', label: 'Active Patients - Descending' },
  { value: 'NAME_ASC', label: 'Facility Name (A to Z)' }
];

const getOptions = (getThemeColor, chartItems, total) => {
  const hasData = total > 0;

  return {
    tooltip: {
      show: hasData,
      padding: [7, 10],
      transitionDuration: 0,
      backgroundColor: getThemeColor('gray-100'),
      borderColor: getThemeColor('gray-300'),
      textStyle: { color: getThemeColor('gray-1100') },
      borderWidth: 1,
      formatter: params =>
        `<strong>${params.data.name}:</strong> ${params.data.value} (${params.percent}%)`
    },
    series: [
      {
        type: 'pie',
        radius: ['70%', '88%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: false,
        emphasis: {
          scale: hasData,
          scaleSize: 4
        },
        itemStyle: {
          borderWidth: 2,
          borderColor: getThemeColor('gray-100')
        },
        label: {
          show: true,
          position: 'center',
          formatter: () => `${total}\nTotal`,
          fontSize: 16,
          fontWeight: '700',
          color: getThemeColor('gray-1100'),
          lineHeight: 20
        },
        data: hasData
          ? chartItems
              .filter(item => item.value > 0)
              .map(item => ({
                name: item.name,
                value: item.value,
                key: item.key,
                itemStyle: { color: item.color }
              }))
          : [
              {
                name: 'No Prescriptions',
                value: 1,
                itemStyle: { color: getThemeColor('gray-300') }
              }
            ]
      }
    ]
  };
};

const OrderData = ({ data = [] }) => {
  const { getThemeColor } = useAppContext();
  const [selectedFacility, setSelectedFacility] = useState({
    value: 'ALL',
    label: 'All Facilities'
  });
  const [showModal, setShowModal] = useState(false);
  const [modalSearch, setModalSearch] = useState('');
  const [modalSort, setModalSort] = useState(MODAL_SORT_OPTIONS[0]);
  const [unverifiedModal, setUnverifiedModal] = useState({
    show: false,
    facilityId: 'ALL',
    facilityName: 'All Facilities',
    bucket: 'ALL'
  });

  const openUnverifiedModal = (facilityId, bucket = 'ALL', facilityName = '') => {
    setUnverifiedModal({
      show: true,
      facilityId: facilityId || 'ALL',
      facilityName:
        facilityName ||
        (facilityId === 'ALL' ? 'All Facilities' : `Facility ${facilityId}`),
      bucket: bucket || 'ALL'
    });
  };

  const safeData = Array.isArray(data) ? data : [];

  // Aggregated totals across all facilities
  const totals = useMemo(() => {
    return safeData.reduce(
      (acc, item) => {
        acc.verified += toNumber(item.no_of_verified_prescriptions);
        acc.unverified += toNumber(item.no_of_unverified_prescriptions);
        acc.within_7 += toNumber(item.unverified_within_7_days);
        acc.days_8_14 += toNumber(item.unverified_8_to_14_days);
        acc.days_15_21 += toNumber(item.unverified_15_to_21_days);
        acc.over_21 += toNumber(item.unverified_over_21_days);
        acc.active_patients += toNumber(item.no_of_active_patients);
        return acc;
      },
      {
        verified: 0,
        unverified: 0,
        within_7: 0,
        days_8_14: 0,
        days_15_21: 0,
        over_21: 0,
        active_patients: 0
      }
    );
  }, [safeData]);

  // Selected facility or aggregated totals
  const selectedData = useMemo(() => {
    if (selectedFacility.value === 'ALL') {
      return {
        facility_name: 'All Facilities',
        verified: totals.verified,
        unverified: totals.unverified,
        within_7: totals.within_7,
        days_8_14: totals.days_8_14,
        days_15_21: totals.days_15_21,
        over_21: totals.over_21,
        active_patients: totals.active_patients
      };
    }

    const facility = safeData.find(
      f => String(f.facility_id) === String(selectedFacility.value)
    );

    return {
      facility_name: facility?.facility_name || `Facility ${selectedFacility.value}`,
      verified: toNumber(facility?.no_of_verified_prescriptions),
      unverified: toNumber(facility?.no_of_unverified_prescriptions),
      within_7: toNumber(facility?.unverified_within_7_days),
      days_8_14: toNumber(facility?.unverified_8_to_14_days),
      days_15_21: toNumber(facility?.unverified_15_to_21_days),
      over_21: toNumber(facility?.unverified_over_21_days),
      active_patients: toNumber(facility?.no_of_active_patients)
    };
  }, [selectedFacility, safeData, totals]);

  const chartItems = useMemo(
    () => [
      {
        key: 'verified',
        name: 'Verified',
        value: selectedData.verified,
        color: '#00d27a'
      },
      {
        key: 'within_7',
        name: 'Unverified within 7 days',
        value: selectedData.within_7,
        color: '#2c7be5'
      },
      {
        key: 'days_8_14',
        name: 'Unverified within 8-14 days',
        value: selectedData.days_8_14,
        color: '#f5803e'
      },
      {
        key: 'days_15_21',
        name: 'Unverified within 15-21 days',
        value: selectedData.days_15_21,
        color: '#ea580c'
      },
      {
        key: 'over_21',
        name: 'Unverified within > 21 days',
        value: selectedData.over_21,
        color: '#e63757'
      }
    ],
    [selectedData]
  );

  const total = chartItems.reduce((acc, item) => acc + item.value, 0);

  const facilityOptions = useMemo(
    () => [
      { value: 'ALL', label: 'All Facilities' },
      ...safeData.map(facility => ({
        value: String(facility.facility_id),
        label: facility.facility_name || `Facility ${facility.facility_id}`
      }))
    ],
    [safeData]
  );

  const filteredModalFacilities = useMemo(() => {
    let list = safeData.map(f => ({
      ...f,
      facility_name: f.facility_name || `Facility ${f.facility_id}`,
      no_of_verified_prescriptions: toNumber(f.no_of_verified_prescriptions),
      no_of_unverified_prescriptions: toNumber(f.no_of_unverified_prescriptions),
      unverified_within_7_days: toNumber(f.unverified_within_7_days),
      unverified_8_to_14_days: toNumber(f.unverified_8_to_14_days),
      unverified_15_to_21_days: toNumber(f.unverified_15_to_21_days),
      unverified_over_21_days: toNumber(f.unverified_over_21_days),
      no_of_active_patients: toNumber(f.no_of_active_patients)
    }));

    if (modalSearch.trim()) {
      const q = modalSearch.toLowerCase();
      list = list.filter(f => f.facility_name.toLowerCase().includes(q));
    }

    return list.sort((a, b) => {
      switch (modalSort.value) {
        case 'OVER_21_DESC':
          return b.unverified_over_21_days - a.unverified_over_21_days;
        case 'UNVERIFIED_DESC':
          return b.no_of_unverified_prescriptions - a.no_of_unverified_prescriptions;
        case 'PATIENTS_DESC':
          return b.no_of_active_patients - a.no_of_active_patients;
        case 'NAME_ASC':
        default:
          return a.facility_name.localeCompare(b.facility_name);
      }
    });
  }, [safeData, modalSearch, modalSort]);

  return (
    <>
      <Card className="h-100">
        {/* Header with Title, Facility Selector & Quick Action */}
        <Card.Header className="d-flex align-items-center justify-content-between flex-wrap gap-2 pb-2">
          <h6 className="mb-0">Prescription Status</h6>

          <div className="d-flex align-items-center gap-2">
            <div style={{ minWidth: '180px' }}>
              <ThemedSelect
                value={selectedFacility}
                onChange={option => setSelectedFacility(option || facilityOptions[0])}
                options={facilityOptions}
                placeholder="Select Facility..."
                isSearchable
                size="sm"
              />
            </div>

            {safeData.length > 1 && (
              <Button
                variant="outline-secondary"
                size="sm"
                className="d-flex align-items-center gap-1 py-1 px-2.5 border-300 fs-11 shadow-none fw-semibold"
                style={{ height: '36px' }}
                onClick={() => setShowModal(true)}
              >
                <FontAwesomeIcon icon="table" className="text-500" />
                <span className="d-none d-sm-inline">View All facilities</span>
                <Badge pill bg="secondary" className="fs-11 py-1 px-1.5 ms-1">
                  {safeData.length}
                </Badge>
              </Button>
            )}
          </div>
        </Card.Header>

        <Card.Body className="pt-2">
          <Row className="align-items-center justify-content-between g-3">
            {/* Left: Distribution text list */}
            <Col xs={12} sm={7} md={7}>
              <div className="d-flex flex-column justify-content-center pe-sm-2">
                {chartItems.map((item, index) => {
                  const percentage =
                    total > 0 ? ((item.value * 100) / total).toFixed(1) : '0.0';
                  const isClickable = item.key !== 'verified';
                  return (
                    <div
                      key={item.key}
                      className={`d-flex align-items-center justify-content-between fw-semibold fs-11 py-1 px-1 rounded ${
                        isClickable ? 'cursor-pointer hover-bg-100' : ''
                      } ${index === 0 ? 'mt-1' : 'mt-1'}`}
                      style={{
                        cursor: isClickable ? 'pointer' : 'default',
                        transition: 'background-color 0.15s ease'
                      }}
                      title={
                        isClickable
                          ? `View ${item.name} for ${selectedFacility.label}`
                          : undefined
                      }
                      onClick={() => {
                        if (isClickable) {
                          openUnverifiedModal(
                            selectedFacility.value,
                            item.key,
                            selectedFacility.label
                          );
                        }
                      }}
                    >
                      <p className="mb-0 d-flex align-items-center text-700">
                        <FontAwesomeIcon
                          icon="circle"
                          className="me-2 flex-shrink-0"
                          style={{ color: item.color, fontSize: '8px' }}
                        />
                        <span>{item.name}</span>
                      </p>
                      <span className="text-nowrap ms-2 d-flex align-items-center gap-1">
                        <span
                          className={
                            isClickable && item.value > 0
                              ? 'text-primary fw-bold text-decoration-underline'
                              : ''
                          }
                        >
                          {item.value}
                        </span>
                        <span className="text-500 fw-normal">({percentage}%)</span>
                        {isClickable && (
                          <FontAwesomeIcon
                            icon="chevron-right"
                            className="text-400 ms-1"
                            style={{ fontSize: '9px' }}
                          />
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Col>

            {/* Right: Pie / Donut Chart */}
            <Col
              xs={12}
              sm={5}
              md={5}
              className="d-flex justify-content-center align-items-center"
            >
              <BasicECharts
                echarts={echarts}
                options={getOptions(getThemeColor, chartItems, total)}
                style={{ width: '8.5rem', height: '8.5rem', cursor: 'pointer' }}
                onEvents={{
                  click: params => {
                    const key = params?.data?.key;
                    if (key && key !== 'verified') {
                      openUnverifiedModal(
                        selectedFacility.value,
                        key,
                        selectedFacility.label
                      );
                    }
                  }
                }}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Facilities Breakdown Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="xl"
      >
        <Modal.Header closeButton className="bg-light py-2.5 px-3">
          <Modal.Title as="h5" className="d-flex align-items-center gap-2 mb-0">
            <FontAwesomeIcon icon="hospital-user" className="text-primary" />
            <span>Prescription Status by Facility</span>
            <Badge pill bg="primary" className="ms-2 fs-11">
              {safeData.length} Facilities
            </Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          <Row className="g-2 mb-3">
            <Col md={7}>
              <InputGroup size="sm">
                <InputGroup.Text className="bg-white border-end-0 text-400">
                  <FontAwesomeIcon icon="search" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search facilities..."
                  className="border-start-0 ps-0"
                  value={modalSearch}
                  onChange={e => setModalSearch(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={5}>
              <ThemedSelect
                options={MODAL_SORT_OPTIONS}
                value={modalSort}
                onChange={option => setModalSort(option || MODAL_SORT_OPTIONS[0])}
                placeholder="Sort by..."
                isSearchable={false}
                size="sm"
              />
            </Col>
          </Row>

          <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
            <Table responsive hover className="fs-11 align-middle mb-0 border">
              <thead className="bg-200 text-800">
                <tr>
                  <th className="py-2 px-3">Facility Name</th>
                  <th className="text-center py-2">Active Patients</th>
                  <th className="text-center py-2 text-success">Verified</th>
                  <th className="text-center py-2 text-danger">Unverified</th>
                  <th className="text-center py-2" style={{ color: '#2c7be5' }}>
                    ≤ 7d
                  </th>
                  <th className="text-center py-2" style={{ color: '#f5803e' }}>
                    8–14d
                  </th>
                  <th className="text-center py-2" style={{ color: '#ea580c' }}>
                    15–21d
                  </th>
                  <th
                    className="text-center py-2 fw-bold"
                    style={{ color: '#e63757' }}
                  >
                    &gt; 21d
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredModalFacilities.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-4 text-500">
                      No matching facilities found.
                    </td>
                  </tr>
                ) : (
                  filteredModalFacilities.map(f => (
                    <tr
                      key={f.facility_id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedFacility({
                          value: String(f.facility_id),
                          label: f.facility_name
                        });
                        setShowModal(false);
                      }}
                    >
                      <td className="fw-bold text-900 py-2 px-3">
                        {f.facility_name}
                      </td>
                      <td className="text-center fw-semibold text-700 py-2">
                        {f.no_of_active_patients}
                      </td>
                      <td className="text-center text-success fw-bold py-2">
                        {f.no_of_verified_prescriptions}
                      </td>
                      <td
                        className="text-center text-danger fw-bold py-2"
                        title={`View all unverified for ${f.facility_name}`}
                        onClick={e => {
                          e.stopPropagation();
                          openUnverifiedModal(
                            String(f.facility_id),
                            'ALL',
                            f.facility_name
                          );
                        }}
                      >
                        <span
                          className={
                            f.no_of_unverified_prescriptions > 0
                              ? 'text-decoration-underline'
                              : ''
                          }
                        >
                          {f.no_of_unverified_prescriptions}
                        </span>
                      </td>
                      <td
                        className="text-center py-2"
                        style={{ color: '#2c7be5' }}
                        title={`View ≤ 7d unverified for ${f.facility_name}`}
                        onClick={e => {
                          if (f.unverified_within_7_days > 0) {
                            e.stopPropagation();
                            openUnverifiedModal(
                              String(f.facility_id),
                              'within_7',
                              f.facility_name
                            );
                          }
                        }}
                      >
                        <span
                          className={
                            f.unverified_within_7_days > 0
                              ? 'fw-bold text-decoration-underline'
                              : ''
                          }
                        >
                          {f.unverified_within_7_days}
                        </span>
                      </td>
                      <td
                        className="text-center py-2"
                        style={{ color: '#f5803e' }}
                        title={`View 8–14d unverified for ${f.facility_name}`}
                        onClick={e => {
                          if (f.unverified_8_to_14_days > 0) {
                            e.stopPropagation();
                            openUnverifiedModal(
                              String(f.facility_id),
                              'days_8_14',
                              f.facility_name
                            );
                          }
                        }}
                      >
                        <span
                          className={
                            f.unverified_8_to_14_days > 0
                              ? 'fw-bold text-decoration-underline'
                              : ''
                          }
                        >
                          {f.unverified_8_to_14_days}
                        </span>
                      </td>
                      <td
                        className="text-center py-2"
                        style={{ color: '#ea580c' }}
                        title={`View 15–21d unverified for ${f.facility_name}`}
                        onClick={e => {
                          if (f.unverified_15_to_21_days > 0) {
                            e.stopPropagation();
                            openUnverifiedModal(
                              String(f.facility_id),
                              'days_15_21',
                              f.facility_name
                            );
                          }
                        }}
                      >
                        <span
                          className={
                            f.unverified_15_to_21_days > 0
                              ? 'fw-bold text-decoration-underline'
                              : ''
                          }
                        >
                          {f.unverified_15_to_21_days}
                        </span>
                      </td>
                      <td
                        className="text-center fw-bold py-2"
                        style={{
                          color:
                            f.unverified_over_21_days > 0
                              ? '#e63757'
                              : 'inherit',
                          backgroundColor:
                            f.unverified_over_21_days > 0
                              ? 'rgba(230, 55, 87, 0.08)'
                              : 'transparent'
                        }}
                        title={`View > 21d unverified for ${f.facility_name}`}
                        onClick={e => {
                          if (f.unverified_over_21_days > 0) {
                            e.stopPropagation();
                            openUnverifiedModal(
                              String(f.facility_id),
                              'over_21',
                              f.facility_name
                            );
                          }
                        }}
                      >
                        <span
                          className={
                            f.unverified_over_21_days > 0
                              ? 'text-decoration-underline'
                              : ''
                          }
                        >
                          {f.unverified_over_21_days}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-light py-2 px-3 d-flex justify-content-between align-items-center">
          <div className="fs-11 text-600">
            Showing <strong>{filteredModalFacilities.length}</strong> of{' '}
            <strong>{safeData.length}</strong> facilities | Total Unverified:{' '}
            <strong
              className="text-danger cursor-pointer text-decoration-underline"
              style={{ cursor: 'pointer' }}
              title="View all unverified across all facilities"
              onClick={() =>
                openUnverifiedModal('ALL', 'ALL', 'All Facilities')
              }
            >
              {totals.unverified}
            </strong>{' '}
            (Over 21 days:{' '}
            <strong
              className="text-danger cursor-pointer text-decoration-underline"
              style={{ cursor: 'pointer' }}
              title="View over 21 days unverified across all facilities"
              onClick={() =>
                openUnverifiedModal('ALL', 'over_21', 'All Facilities')
              }
            >
              {totals.over_21}
            </strong>
            )
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Unverified Prescriptions Detail Modal */}
      <UnverifiedPrescriptionModal
        show={unverifiedModal.show}
        onHide={() =>
          setUnverifiedModal(prev => ({ ...prev, show: false }))
        }
        initialFacilityId={unverifiedModal.facilityId}
        initialBucket={unverifiedModal.bucket}
        facilityOptions={facilityOptions}
      />
    </>
  );
};

OrderData.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      group_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      facility_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      facility_name: PropTypes.string,
      no_of_verified_prescriptions: PropTypes.number,
      no_of_unverified_prescriptions: PropTypes.number,
      unverified_within_7_days: PropTypes.number,
      unverified_8_to_14_days: PropTypes.number,
      unverified_15_to_21_days: PropTypes.number,
      unverified_over_21_days: PropTypes.number,
      no_of_active_patients: PropTypes.number
    })
  )
};

export default OrderData;
